// Work — one table for every piece of work. A "lens" is a saved filter over `kind`
// and `state`, shown as tabs above the list, never a separate list to build: demos,
// deliverables, infrastructure and the set-aside shelf are all this page.
//
// Inactive is not archived. It stays listed, hatched, searchable and mentionable,
// and drops out of every rollup — which is what "set aside" actually means.

import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Button, EmptyState, Pill } from '../components/common'
import { DataTable, Num, type Column } from '../components/DataTable'
import { Icon } from '../components/Icon'
import { LinkCell } from '../components/Links'
import { FilterInput, View, type Lens } from '../components/Page'
import { useCreateEntity } from '../components/useCreate'
import { absoluteOf, money, relativeShort } from '../lib/format'
import { labelOf, pluralOf } from '../lib/model'
import {
  dealsForProject,
  entitiesOfKind,
  fieldStr,
  informs,
  lastTouch,
  linkedOfKind,
  titleOf,
  type EntityRecord,
} from '../lib/reducer'
import { useStore } from '../lib/store'

export function ProjectsPage() {
  const { lens = '' } = useParams()
  const { state, config, version } = useStore()
  const create = useCreateEntity()
  const navigate = useNavigate()
  const lenses = useWorkLenses()
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')

  const kindDef = config.projectKinds.find((k) => k.key === lens)

  const rows = useMemo(() => {
    void version
    const all = entitiesOfKind(state, 'project')
    const filtered = all.filter((p) => {
      if (kindDef) return fieldStr(p, 'kind') === kindDef.key
      return true
    })
    const q = query.trim().toLowerCase()
    if (!q) return filtered
    return filtered.filter(
      (p) =>
        titleOf(state, p.id).toLowerCase().includes(q) ||
        fieldStr(p, 'summary').toLowerCase().includes(q) ||
        (labelOf(config.projectKinds, fieldStr(p, 'kind'))?.label ?? '').toLowerCase().includes(q),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, query, lens, config])

  const columns = useMemo<Column<EntityRecord>[]>(() => {
    const deals = (p: EntityRecord) => dealsForProject(state, p.id)
    const wonValue = (p: EntityRecord) =>
      deals(p)
        .filter((d) => config.stages.find((s) => s.key === fieldStr(d, 'stage'))?.kind === 'won')
        .reduce((sum, d) => sum + (Number(d.fields.value) || 0), 0)

    return [
      {
        // Name first: the pill used to lead, which made every row start with the same
        // few words and pushed the name you were scanning for into second place.
        key: 'name',
        label: 'Name',
        // Capped once Link is on the row, so the slack goes to the link instead of to
        // whitespace after the title. Left uncapped this is the table's only flexible
        // column, which handed it every spare pixel: at 1000px the name held 545px of a
        // 986px table while Link, the thing you actually click, had been dropped for
        // want of 190. Below that the cap lifts, because with Link gone the leftover
        // would go to the numeric columns and push them away from their own text.
        widthClass: '@3xl:w-[32%]',
        value: (p) => titleOf(state, p.id),
        render: (p) => (
          <span className="flex items-center gap-2">
            <span className="truncate font-medium">{titleOf(state, p.id) || 'Untitled work'}</span>
            {p.archived && <span className="badge-archived">Archived</span>}
          </span>
        ),
      },
      {
        key: 'kind',
        label: 'Kind',
        width: 118,
        value: (p) => labelOf(config.projectKinds, fieldStr(p, 'kind'))?.label ?? 'zz',
        render: (p) => {
          const k = labelOf(config.projectKinds, fieldStr(p, 'kind'))
          return k ? <Pill def={k} label={k.label} /> : null
        },
      },
      {
        // Both, because they answer different questions. The link is where the
        // project's context lives — the repo, the drive, the recording. The count is
        // how much it has delivered. Removing the link column when projects briefly
        // lost the field left "where is this thing" unanswerable from the list.
        key: 'link',
        label: 'Link',
        sortable: false,
        hideBelow: 896,
        render: (p) => <LinkCell value={fieldStr(p, 'links') || fieldStr(p, 'url')} />,
      },
      {
        key: 'artifacts',
        label: 'Artifacts',
        align: 'right',
        width: 88,
        hideBelow: 768,
        value: (p) => linkedOfKind(state, p.id, 'artifact').length,
        render: (p) => <Num value={linkedOfKind(state, p.id, 'artifact').length} />,
      },
      {
        key: 'deals',
        label: 'Deals',
        align: 'right',
        width: 62,
        hideBelow: 448,
        value: (p) => deals(p).length,
        render: (p) => <Num value={deals(p).length} hue="--ember" />,
      },
      {
        key: 'won',
        label: 'Won',
        align: 'right',
        width: 92,
        value: (p) => wonValue(p),
        render: (p) => <Num value={money(wonValue(p), '·')} hue="--good" />,
      },
      {
        key: 'downstream',
        label: 'Downstream',
        align: 'right',
        width: 100,
        hideBelow: 1024,
        value: (p) => informs(state, p.id).length,
        render: (p) => <Num value={informs(state, p.id).length} hue="--brass" />,
      },
      {
        key: 'touched',
        label: 'Touched',
        align: 'right',
        width: 96,
        hideBelow: 672,
        // Sorts on the timestamp, reads as prose.
        value: (p) => lastTouch(state, p.id),
        render: (p) => (
          <span
            className="num"
            style={{ fontSize: 'var(--text-label)', color: 'var(--faint)' }}
            title={absoluteOf(lastTouch(state, p.id))}
          >
            {relativeShort(lastTouch(state, p.id))}
          </span>
        ),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, config])

  const totalProjects = entitiesOfKind(state, 'project').length

  return (
    <View
      lenses={lenses}
      actions={
        <>
          <FilterInput value={query} onChange={setQuery} />
          <Button variant="work" onClick={() => setCreating(true)}>
            <Icon name="Plus" size={14} /> New
          </Button>
        </>
      }
    >
      {creating && (
        <div
          className="rounded-control mb-4 flex items-center gap-2 border p-2"
          style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
        >
          <Icon name="Boxes" size={16} style={{ color: 'var(--marine)' }} />
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) {
                void create('project', name, kindDef ? { kind: kindDef.key } : undefined)
                setName('')
                setCreating(false)
              }
              if (e.key === 'Escape') setCreating(false)
            }}
            onBlur={() => setCreating(false)}
            placeholder={kindDef ? `New ${kindDef.label.toLowerCase()}` : 'What are we building?'}
            className="text-body flex-1 bg-transparent outline-none"
          />
          <span className="meta">Enter to create</span>
        </div>
      )}

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(p) => p.id}
        onOpen={(p) => navigate(`/e/${p.id}`)}
        onOpenFull={(p) => navigate(`/e/${p.id}`)}
        openLabel={(p) => titleOf(state, p.id)}
        storageKey="crum.work.sort"
        initialSort={{ key: 'touched', dir: 'desc' }}
        empty={
          <EmptyState
            icon="Boxes"
            title={totalProjects === 0 ? 'No work logged yet' : 'Nothing in this lens'}
            hint={
              totalProjects === 0
                ? 'Anything you build: demos, artifacts, infrastructure, research.'
                : undefined
            }
            action={
              totalProjects === 0 ? (
                <Button variant="work" onClick={() => setCreating(true)}>
                  <Icon name="Plus" size={14} /> New work
                </Button>
              ) : undefined
            }
          />
        }
      />
    </View>
  )
}

/**
 * The lenses over the work table. They're real routes, so a lens is linkable and the
 * back button works, and they live in the view bar with every other view's lenses —
 * which is what makes the row read as "one table, several labels" instead of several
 * lists.
 */
function useWorkLenses(): Lens[] {
  const { state, config, version } = useStore()
  void version

  const projects = entitiesOfKind(state, 'project')

  return [
    { key: '/work', to: '/work', label: 'All', count: projects.length, color: 'marine' },
    ...config.projectKinds
      .map((k) => ({
        key: `/work/${k.key}`,
        to: `/work/${k.key}`,
        label: pluralOf(k),
        count: projects.filter((p) => fieldStr(p, 'kind') === k.key).length,
        color: k.color,
      }))
      // An empty kind is vocabulary, not a view: don't spend a chip on it.
      .filter((lens) => lens.count > 0),
  ]
}
